import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Bell, Loader2, Send, AlertTriangle, Users } from "lucide-react";

const SEGMENTS = [
  { id: "all", label: "All Subscribers" },
  { id: "services", label: "Services Only" },
  { id: "events", label: "Events Only" },
];

const PushBroadcast = () => {
  const [stats, setStats] = useState({ configured: false, subscriber_count: 0 });
  const [history, setHistory] = useState([]);
  const [form, setForm] = useState({ title: "", message: "", url: "", segment: "all" });
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const [s, h] = await Promise.all([
        api.get("/admin/push/stats"),
        api.get("/admin/push/history"),
      ]);
      setStats(s.data); setHistory(h.data.items || []);
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.warn("[push] failed to load stats/history", e);
    }
  };
  useEffect(() => { load(); }, []);

  const send = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.message.trim()) {
      toast.error("Title and message are required."); return;
    }
    if (!window.confirm(`Send to ${stats.subscriber_count} subscriber${stats.subscriber_count === 1 ? "" : "s"}?`)) return;
    setSending(true);
    try {
      await api.post("/admin/push/broadcast", form);
      toast.success("Broadcast sent.");
      setForm({ title: "", message: "", url: "", segment: "all" });
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Send failed.");
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-8" data-testid="admin-push">
      <header>
        <div className="overline">Broadcasts</div>
        <h1 className="font-serif-display text-4xl sm:text-5xl text-[#F6F0E6] mt-1">
          Send the <span className="italic gold-text">fire signal</span>
        </h1>
        <p className="text-[#D9D9D9]/70 text-sm mt-2 max-w-2xl">
          Push notifications go directly to subscriber devices via OneSignal.
        </p>
      </header>

      {!stats.configured && (
        <div className="bento-card !p-5 border-[#E8452C]/40 flex items-start gap-3" data-testid="push-not-configured">
          <AlertTriangle className="h-5 w-5 text-[#E8452C] flex-shrink-0 mt-0.5" />
          <div className="text-sm text-[#F6F0E6]">
            OneSignal is not configured yet. Add <code className="text-[#C9A44C]">ONESIGNAL_APP_ID</code> and{" "}
            <code className="text-[#C9A44C]">ONESIGNAL_REST_API_KEY</code> in the backend <code>.env</code>, then restart the backend.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <form onSubmit={send} className="lg:col-span-7 bento-card !p-7 space-y-5" data-testid="push-form">
          <span className="glow-blob" />
          <div>
            <label className="field-label">Title</label>
            <input className="input-fire" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Service starts in 30 minutes" maxLength={120} data-testid="push-title" />
          </div>
          <div>
            <label className="field-label">Message</label>
            <textarea rows={3} className="input-fire" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="What do they need to know?" maxLength={300} data-testid="push-message" />
          </div>
          <div>
            <label className="field-label">URL on click <span className="opacity-60 normal-case tracking-normal">(optional)</span></label>
            <input className="input-fire" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://…" data-testid="push-url" />
          </div>
          <div>
            <label className="field-label">Audience</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {SEGMENTS.map((s) => (
                <button key={s.id} type="button" onClick={() => setForm({ ...form, segment: s.id })}
                  className={`px-4 py-2 rounded-full border text-xs tracking-[0.18em] uppercase ${form.segment === s.id ? "border-[#C9A44C] bg-[#C9A44C]/10 text-[#F6F0E6]" : "border-[#C9A44C]/30 text-[#D9D9D9]"}`}
                  data-testid={`push-segment-${s.id}`}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <button type="submit" disabled={sending || !stats.configured} className="btn-fire w-full" data-testid="push-send">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {sending ? "Sending…" : "Send Broadcast"}
          </button>
        </form>

        <div className="lg:col-span-5 space-y-5">
          <div className="bento-card !p-6" data-testid="push-subs">
            <span className="glow-blob" />
            <div className="flex items-center gap-3">
              <div className="icon-badge"><Users className="h-5 w-5 text-[#C9A44C]" /></div>
              <div>
                <div className="overline">Subscribers</div>
                <div className="font-serif-display text-4xl text-[#F6F0E6] mt-1">{stats.subscriber_count}</div>
              </div>
            </div>
          </div>
          <div className="bento-card !p-6" data-testid="push-history">
            <span className="glow-blob" />
            <div className="overline mb-4">Recent broadcasts</div>
            {history.length === 0 ? (
              <div className="text-sm text-[#D9D9D9]/60">No broadcasts sent yet.</div>
            ) : (
              <ul className="space-y-3">
                {history.map((h) => (
                  <li key={h.id} className="border-b border-[#C9A44C]/10 pb-2 last:border-0">
                    <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-[#C9A44C]"><Bell className="h-3 w-3" /> {h.segment}</div>
                    <div className="text-sm text-[#F6F0E6] mt-0.5">{h.title}</div>
                    <div className="text-xs text-[#D9D9D9]/70 line-clamp-2">{h.message}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PushBroadcast;
